// Customer-facing copy for a declined Dodo payment, grouped from Dodo's error codes
// (docs.dodopayments.com/api-reference/transaction-failures). Dodo's own error_message is written
// for the merchant ("Ask the customer to retry"), so it is never shown.
export type CheckoutDeclineReason =
  | 'declined'
  | 'insufficient_funds'
  | 'expired_card'
  | 'verification'
  | 'card_details'
  | 'card_blocked'
  | 'payment_method'
  | 'temporary';

const DECLINE_REASON_BY_CODE: Record<string, CheckoutDeclineReason> = {
  INSUFFICIENT_FUNDS: 'insufficient_funds',
  EXPIRED_CARD: 'expired_card',
  AUTHENTICATION_FAILURE: 'verification',
  AUTHENTICATION_REQUIRED: 'verification',
  AUTHENTICATION_TIMEOUT: 'verification',
  INCORRECT_CVC: 'card_details',
  INVALID_CVC: 'card_details',
  INCORRECT_NUMBER: 'card_details',
  INVALID_CARD_NUMBER: 'card_details',
  INVALID_CARD_OWNER: 'card_details',
  INVALID_EXPIRY_YEAR: 'card_details',
  INVALID_PIN: 'card_details',
  INVALID_UPI_ID: 'card_details',
  // Dodo: never tell the customer the real reason for these.
  FRAUDULENT: 'card_blocked',
  LOST_CARD: 'card_blocked',
  PICKUP_CARD: 'card_blocked',
  STOLEN_CARD: 'card_blocked',
  PAYMENT_METHOD_PROVIDER_DECLINED: 'payment_method',
  PAYMENT_METHOD_UNSUPPORTED: 'payment_method',
  PROVIDER_UNSUPPORTED: 'payment_method',
  NETWORK_ERROR: 'temporary',
  NETWORK_TIMEOUT: 'temporary',
  PROCESSING_ERROR: 'temporary',
  TRY_AGAIN_LATER: 'temporary',
  CARD_VELOCITY_EXCEEDED: 'temporary',
};

export function getCheckoutDeclineReason(
  errorCode: string | null | undefined,
): CheckoutDeclineReason {
  return (errorCode && DECLINE_REASON_BY_CODE[errorCode.toUpperCase()]) || 'declined';
}

export const CHECKOUT_DECLINE_TITLE = 'Your payment didn’t go through';

export const CHECKOUT_DECLINE_MESSAGES: Record<CheckoutDeclineReason, string> = {
  declined:
    'Your bank declined this card and you haven’t been charged. Try again or use a different card.',
  insufficient_funds:
    'This card doesn’t have enough funds and you haven’t been charged. Use a different card or try again later.',
  expired_card: 'This card has expired and you haven’t been charged. Use a different card.',
  verification:
    'Your bank’s verification step wasn’t completed. Try again and finish the verification.',
  card_details:
    'Some card details were incorrect. Check the card number, expiry date and security code, then try again.',
  card_blocked: 'Your card was declined. Contact your bank or use another card.',
  payment_method: 'This payment method can’t be used for this purchase. Use a different card.',
  temporary:
    'The payment couldn’t be processed right now and you haven’t been charged. Try again in a few minutes.',
};

// What a page says when Dodo checkout sends the user back to it, in the dashboard and the builder.
export const CHECKOUT_RETURN_COPY = {
  planActive: (planName: string | null) =>
    planName ? `You’re on the ${planName} plan` : 'Your new plan is active',
  collaboratorsUnlocked: 'You can add collaborators now.',
  creditsAdded: 'AI credits added',
  creditsAddedDescription: 'Your purchased credits are ready to use.',
  paymentReceived: 'Payment received',
  paymentReceivedDescription:
    'Your plan updates in a minute. Refresh the page if it hasn’t changed.',
  stillConfirming: 'We’re still confirming your payment',
  stillConfirmingPlan:
    'Your plan changes once your bank confirms the payment. Refresh the page in a few minutes to check.',
  stillConfirmingCredits:
    'Your credits are added once your bank confirms the payment. Refresh the page in a few minutes to check.',
  cancelled: 'Checkout canceled',
  cancelledDescription: 'You weren’t charged.',
  tryAgain: 'Try again',
  refresh: 'Refresh page',
} as const;

/** Query params checkout adds to a return URL; stripped before a page is used as a return URL again. */
export const CHECKOUT_RETURN_PARAMS = [
  'checkout',
  'payment_id',
  'subscription_id',
  'status',
  'email',
] as const;

/** The page to come back to after checkout, without the params a previous checkout left on it. */
export function toCheckoutReturnUrl(href: string): string {
  const url = new URL(href);
  CHECKOUT_RETURN_PARAMS.forEach((key) => url.searchParams.delete(key));
  return url.toString();
}
